import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

export const BetaGatePage: React.FC = () => {
  const { darkMode } = useTheme();
  const { approveBetaAccess, handleSignIn, setAppState } = useAuth();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;

    setIsSubmitting(true);
    setError('');

    const approved = await approveBetaAccess(code);
    if (approved) {
      // Code accepted — proceed to Google OAuth sign-in
      handleSignIn();
    } else {
      setError('Invalid invite code. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`min-h-[100dvh] flex items-center justify-center px-4 py-8 ${
      darkMode
        ? 'bg-slate-950'
        : 'bg-[#f8f9ff]'
    }`}>
      <div className={`w-full max-w-md mx-4 p-8 rounded-3xl backdrop-blur-xl ${
        darkMode ? 'bg-slate-800/70 border border-slate-700/50' : 'bg-white/70 border border-white/40'
      } shadow-2xl`}>
        <div className="text-center mb-6">
          <div className="rounded-xl p-1.5 mx-auto mb-3 w-fit bg-gradient-to-b from-white/90 to-white border border-white/60 shadow-[0_2px_8px_rgba(0,0,0,0.12),inset_0_1px_2px_rgba(255,255,255,0.9),inset_0_-1px_3px_rgba(0,0,0,0.06)] backdrop-blur-xl">
            <img src="/logo.png" alt="TallyUp Finance" className="h-8 w-8" />
          </div>
          <h1 className={`text-3xl font-bold leading-tight ${darkMode ? 'text-white' : 'text-slate-900'}`}>
            TallyUp
          </h1>
          <p className={`text-sm font-medium tracking-[0.15em] mb-2 ${darkMode ? 'text-violet-300' : 'text-violet-600'}`}>
            Finance
          </p>
          <p className={`text-base ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
            TallyUp Finance is currently in closed beta. Enter your invite code to get started.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            value={code}
            onChange={(e) => { setCode(e.target.value); setError(''); }}
            placeholder="Enter invite code"
            className={`w-full px-4 py-3 rounded-xl text-center text-lg font-mono tracking-wider ${
              darkMode
                ? 'bg-white/10 border border-white/20 text-white placeholder-slate-500 focus:border-violet-400'
                : 'bg-white border border-slate-300 text-slate-900 placeholder-slate-400 focus:border-violet-500'
            } outline-none transition-colors`}
            autoFocus
          />

          {error && (
            <p className={`text-sm text-center ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={isSubmitting || !code.trim()}
            className={`w-full px-6 py-3 rounded-xl font-semibold transition-all ${
              isSubmitting || !code.trim()
                ? 'bg-violet-600/50 text-white/50 cursor-not-allowed'
                : 'bg-violet-600 hover:bg-violet-700 text-white'
            }`}
          >
            {isSubmitting ? 'Verifying...' : 'Continue'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => setAppState('landing')}
            className={`text-sm ${darkMode ? 'text-slate-400 hover:text-slate-300' : 'text-slate-500 hover:text-slate-700'} transition-colors`}
          >
            &larr; Back
          </button>
        </div>
      </div>
    </div>
  );
};
